import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LuChevronLeft, LuPencil, LuTrash2, LuPlus } from 'react-icons/lu';

import { QuizzesAPI } from '~/services/db/quizzes';
import { logEvent } from '~/config/firebase.config';

import cockpit from '../Animator.module.css';
import styles from './QuizStudio.module.css';
import { AiQuizComposer } from './AiQuiz';
import { deleteDraft, draftIsStale, editableFingerprint, listDrafts, QuizDraft, timeAgo } from './quizDraft';
import { studioQuizFromDoc } from './quizTypes';

type ListedQuiz = { id: string; title: string; questionCount: number; updatedAt?: Date };

/**
 * Studio Quiz — porte d'entrée de la création (mobile ET desktop) :
 * la création en une phrase (IA) en tête, l’éditeur manuel en second,
 * puis la liste des quiz de l’établissement.
 * Créer un quiz est une tâche à part entière, séparée du pilotage (cockpit).
 */
export function QuizStudioList() {
    const navigate = useNavigate();
    const { clientID } = useParams();
    const [quizzes, setQuizzes] = useState<ListedQuiz[] | null>(null);
    const [pendingDelete, setPendingDelete] = useState<ListedQuiz | null>(null);
    // Brouillons non enregistrés de l'éditeur (ce navigateur seulement).
    const [drafts, setDrafts] = useState<QuizDraft[]>(() => (clientID ? listDrafts(clientID) : []));
    const [pendingDiscard, setPendingDiscard] = useState<string | null>(null);
    // Date de version de chaque quiz enregistré (avertissement « version plus récente »).
    const [savedUpdatedAt, setSavedUpdatedAt] = useState<Map<string, unknown>>(new Map());
    useEffect(() => {
        setDrafts(clientID ? listDrafts(clientID) : []);
    }, [clientID]);
    const resumeDraft = (draft: QuizDraft) => {
        if (draft.isNew)
            navigate(`/animator/${clientID}/quizzes/new`, { state: { draft: draft.quiz, restored: true } });
        else navigate(`/animator/${clientID}/quizzes/${draft.quiz.id}`);
    };
    const discardDraft = (quizId: string) => {
        if (!clientID) return;
        deleteDraft(clientID, quizId);
        setDrafts(listDrafts(clientID));
        setPendingDiscard(null);
    };

    const load = () => {
        if (!clientID) return;
        QuizzesAPI.retrieveQuizzes(clientID, 'unquiz', 0)
            .then((list) => {
                setQuizzes(
                    list.map((q) => ({
                        id: q.id,
                        title: q.title || q.id,
                        questionCount: q.questions?.length ?? 0,
                        updatedAt: q.updatedAt,
                    }))
                );
                setSavedUpdatedAt(new Map(list.map((q) => [q.id, q.updatedAt])));
                // Brouillon IDENTIQUE à la version enregistrée : plus rien à protéger.
                // Un brouillon différent n'est jamais retiré tout seul (même si une version plus récente existe).
                const byId = new Map(list.map((q) => [q.id, q]));
                listDrafts(clientID)
                    .filter((d) => {
                        const doc = byId.get(d.quiz.id);
                        return !!doc && editableFingerprint(studioQuizFromDoc(doc)) === editableFingerprint(d.quiz);
                    })
                    .forEach((d) => deleteDraft(clientID, d.quiz.id));
                setDrafts(listDrafts(clientID));
            })
            .catch(() => setQuizzes([]));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [clientID]);

    const confirmDelete = async () => {
        if (!clientID || !pendingDelete) return;
        logEvent('studio_delete_quiz', clientID, { clientID, quizId: pendingDelete.id });
        try {
            await QuizzesAPI.deleteQuiz(clientID, 'unquiz', pendingDelete.id);
            // Supprimer un quiz supprime aussi son brouillon (sinon « Reprendre » le ferait revenir).
            deleteDraft(clientID, pendingDelete.id);
            setDrafts(listDrafts(clientID));
        } finally {
            setPendingDelete(null);
            load();
        }
    };

    return (
        <div className={cockpit.page}>
            <div className={cockpit.shell}>
                <header className={cockpit.header}>
                    <button
                        type="button"
                        className={cockpit.back}
                        aria-label="Retour au pilotage"
                        onClick={() => navigate(`/animator/${clientID}`)}
                    >
                        <LuChevronLeft />
                    </button>
                    <div className={cockpit.headTitles}>
                        <span className={cockpit.headKicker}>Studio Quiz</span>
                        <h1 className={cockpit.title}>Mes quiz</h1>
                    </div>
                </header>

                {drafts.map((d) => (
                    <div key={d.quiz.id} className={styles.draftRow}>
                        <span className={styles.draftText}>
                            <strong>Brouillon non enregistré :</strong> « {d.quiz.title.trim() || 'Sans titre'} » ·{' '}
                            {d.quiz.questions.length} question{d.quiz.questions.length > 1 ? 's' : ''} ·{' '}
                            {timeAgo(d.savedAt)}
                            {savedUpdatedAt.has(d.quiz.id) &&
                                draftIsStale(d, savedUpdatedAt.get(d.quiz.id)) &&
                                ' · une version plus récente existe'}
                        </span>
                        <span className={styles.draftActions}>
                            <button type="button" className={styles.draftResume} onClick={() => resumeDraft(d)}>
                                Reprendre
                            </button>
                            {pendingDiscard === d.quiz.id ? (
                                <button
                                    type="button"
                                    className={`${styles.draftDiscard} ${styles.draftDiscardConfirm}`}
                                    onClick={() => discardDraft(d.quiz.id)}
                                >
                                    Supprimer pour de bon
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.draftDiscard}
                                    onClick={() => setPendingDiscard(d.quiz.id)}
                                >
                                    Supprimer
                                </button>
                            )}
                        </span>
                    </div>
                ))}

                {/* Créer son quiz en une phrase (IA) d'abord, l'éditeur manuel en second */}
                <AiQuizComposer
                    onSubmit={(request) => navigate(`/animator/${clientID}/quizzes/express`, { state: { request } })}
                />
                <button
                    type="button"
                    className={styles.manualLink}
                    onClick={() => navigate(`/animator/${clientID}/quizzes/new`)}
                >
                    <LuPencil /> ou créer à la main, question par question
                </button>

                {/* La bibliothèque de l’établissement */}
                <h2 className={styles.sectionTitle}>Dans ma bibliothèque {quizzes ? `(${quizzes.length})` : ''}</h2>
                {!quizzes && <p className={cockpit.muted}>Chargement…</p>}
                {quizzes && quizzes.length === 0 && (
                    <p className={cockpit.muted}>
                        Aucun quiz pour l’instant — décrivez le premier en une phrase ci-dessus.
                    </p>
                )}
                {quizzes?.map((q) => (
                    <div key={q.id} className={styles.quizRow}>
                        <button
                            type="button"
                            className={styles.quizRowMain}
                            onClick={() => navigate(`/animator/${clientID}/quizzes/${q.id}`)}
                        >
                            <span className={styles.quizRowTitle}>{q.title}</span>
                            <span className={styles.quizRowMeta}>{q.questionCount} questions</span>
                        </button>
                        <button
                            type="button"
                            className={styles.quizRowDelete}
                            aria-label={`Supprimer ${q.title}`}
                            onClick={() => setPendingDelete(q)}
                        >
                            <LuTrash2 />
                        </button>
                    </div>
                ))}

                {quizzes && quizzes.length > 0 && (
                    <button
                        type="button"
                        className={styles.addGhost}
                        onClick={() => navigate(`/animator/${clientID}/quizzes/new`)}
                    >
                        <LuPlus /> Nouveau quiz
                    </button>
                )}
            </div>

            {pendingDelete && (
                <div className={cockpit.modalScrim} role="dialog" aria-modal="true">
                    <div className={cockpit.modal}>
                        <p className={cockpit.modalTitle}>Supprimer ce quiz ?</p>
                        <p className={cockpit.modalBody}>
                            « <strong>{pendingDelete.title}</strong> » sera supprimé définitivement.
                        </p>
                        <div className={cockpit.modalActions}>
                            <button type="button" className={cockpit.btnGhost} onClick={() => setPendingDelete(null)}>
                                Annuler
                            </button>
                            <button type="button" className={cockpit.btnDanger} onClick={confirmDelete}>
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
